import type { ErrorInfo } from 'react';

import { ensureError } from '@infinex-private/ts-utils/error';
import type {
  ErrorBoundaryContext,
  ErrorEvent,
} from '@infinex-private/ui-react-error-boundary';
import { ERROR_BOUNDARY_CUSTOM_EVENT_TYPE } from '@infinex-private/ui-react-error-boundary';

import type { ErrorContext, ReporterConfig } from './types';

export interface IReporter {
  init(config: ReporterConfig): void;

  /**
   * Error reporting function that accepts an error and optional context.
   * Modeled after Datadog RUM's addError method.
   */
  reportError(error: unknown, context?: ErrorContext): void;

  /**
   * Handler triggered when error boundary error occurs
   */
  onBoundaryError(
    error: Error,
    errorInfo: ErrorInfo,
    errorContext: ErrorBoundaryContext
  ): void;
}

/**
 * Observability Reporter
 *
 * Use it to report observability events, such as errors, in a RUM agnostic way.
 */
class Reporter implements IReporter {
  private static instance: IReporter | null = null;

  private config: ReporterConfig | null = null;
  private errorQueue: Array<{
    error: Error;
    context: ErrorContext;
  }> = [];

  public constructor() {
    globalThis.addEventListener?.(
      ERROR_BOUNDARY_CUSTOM_EVENT_TYPE,
      (event: Event) => {
        const { error, errorInfo, context, ...rest } = (event as ErrorEvent)
          .detail;
        const normalizedError = ensureError(error);
        this.onBoundaryError(normalizedError, errorInfo, {
          ...rest,
          ...context,
        });
      }
    );
  }

  public static getInstance(): IReporter {
    if (!Reporter.instance) {
      Reporter.instance = new Reporter();
    }
    return Reporter.instance;
  }

  public init(config: ReporterConfig): void {
    if (!!this.config) {
      throw new Error('Reporter already initialized.');
    }

    this.config = config;
    this.flushErrorQueue();
  }

  public reportError(error: unknown, context?: ErrorContext): void {
    const normalizedError = ensureError(error);
    this.sendError(normalizedError, context);
  }

  public onBoundaryError(
    error: Error,
    errorInfo: ErrorInfo,
    context: ErrorBoundaryContext
  ): void {
    if (this.config) {
      this.config.onBoundaryError(error, errorInfo, context);
    } else {
      this.errorQueue.push({ error, context });
    }
  }

  private sendError(error: Error, context: ErrorContext = {}): void {
    if (!this.config) {
      this.errorQueue.push({ error, context });
      return;
    }

    try {
      this.config.reportError(error, context);
    } catch (reportError) {
      // eslint-disable-next-line no-console
      console.error('Failed to report error:', reportError);
    }
  }

  private flushErrorQueue(): void {
    if (!this.config) {
      return;
    }

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    errors.forEach((queuedError) =>
      this.sendError(queuedError.error, queuedError.context)
    );
  }
}

export const reporter: IReporter = Reporter.getInstance();

export const _getReporterForTesting = (): IReporter => new Reporter();
